/**
 * API Credentials Functions
 * Frontend API para gestión de credenciales reusables
 */

import { API_CONFIG } from '../../../config/constants';

export interface ApiCredential {
  id: string;
  name: string;
  empresaId: string;
  authType: 'TOKEN_SIMPLE' | 'BEARER_TOKEN' | 'OAUTH_CLIENT_CREDENTIALS';
  status: 'ACTIVE' | 'INACTIVE';
  
  // Flags de existencia de secretos
  hasClientId?: boolean;
  hasClientSecret?: boolean;
  hasTokenUrl?: boolean;
  hasBearerToken?: boolean;
  hasTokenBody?: boolean;
  
  // Metadata (no secretos) - OK para exponer
  tokenUrl?: string | null;
  tokenUrlOAuth?: string | null;
  tokenMethod?: 'GET' | 'POST' | null;
  scope?: string | null;
  
  createdAt: string;
  updatedAt: string;
}

export interface CreateApiCredentialData {
  name: string;
  authType: 'TOKEN_SIMPLE' | 'BEARER_TOKEN' | 'OAUTH_CLIENT_CREDENTIALS';
  
  // TOKEN_SIMPLE
  tokenUrl?: string;
  tokenMethod?: 'GET' | 'POST';
  tokenParamName?: string;
  tokenBody?: Record<string, any>;
  
  // OAUTH_CLIENT_CREDENTIALS
  clientId?: string;
  clientSecret?: string;
  scope?: string;
  tokenUrlOAuth?: string;
  
  // BEARER_TOKEN
  bearerToken?: string;
  
  // Configuración adicional
  extraHeaders?: Record<string, string>;
}

/**
 * Listar todas las credenciales de la empresa
 */
export const getApiCredentials = async (): Promise<ApiCredential[]> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}/api/credentials`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error loading credentials');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Crear nueva credencial
 */
export const createApiCredential = async (data: CreateApiCredentialData): Promise<ApiCredential> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}/api/credentials`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error creating credential');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Obtener credencial específica
 */
export const getApiCredential = async (id: string): Promise<ApiCredential> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}/api/credentials/${id}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error loading credential');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Actualizar credencial existente
 */
export const updateApiCredential = async (id: string, data: Partial<CreateApiCredentialData>): Promise<ApiCredential> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}/api/credentials/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error updating credential');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Eliminar credencial
 */
export const deleteApiCredential = async (id: string): Promise<void> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}/api/credentials/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error deleting credential');
  }
};

/**
 * Testear credencial
 */
export const testApiCredential = async (id: string): Promise<{success: boolean; message: string; authType: string}> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}/api/credentials/${id}/test`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Error testing credential');
  }

  return response.json();
};
